import {
    calibrationFingerprint,
    currentCalibrationDeviceIdentity,
    defaultColourCorrection,
    guessCalibrationMeasurements,
    normalizeDevicePixelRatio,
    resolveStoredCalibration,
} from "../../../app/shared/lib/calibration_core";

export enum AnaglyphOrientation {
  RedCyan = "red-cyan",
  CyanRed = "cyan-red",
  RedGreen = "red-green",
  GreenRed = "green-red",
}

export interface ColourCalibration {
  red_brightness: number;
  blue_brightness: number;
  green_brightness: number;
}

export class Calibration {
    id: string | null = null;
    user_id: string | null = null;
    width_px: number;
    height_px: number;
    pixel_ratio: number;
    height_cm: number;
    distance_cm: number;
    anaglyph_orientation: AnaglyphOrientation;
    colour_correction: ColourCalibration;
    fingerprint: string;
    no_touch_screen: boolean | null;

    constructor(default_anaglyph_orientation = AnaglyphOrientation.CyanRed) {
        const guess = guessCalibrationMeasurements();
        this.id = '';
        this.width_px = guess.width_px;
        this.height_px = guess.height_px;
        this.pixel_ratio = guess.pixel_ratio;
        this.height_cm = guess.height_cm;
        this.distance_cm = guess.distance_cm;
        this.anaglyph_orientation = default_anaglyph_orientation;
        this.colour_correction = defaultColourCorrection();
        this.fingerprint = guess.fingerprint;
        this.no_touch_screen = null;
    }

    // Load the calibration from local storage, if it exists and fingerprint matches, otherwise return null
    static load(): Calibration | null {
        const calibration = resolveStoredCalibration(
            [],
            window.localStorage['opticalgym_calibration'],
        );
        return calibration ? Calibration.fromJson(calibration) : null;
    }

    setDefaultValues(default_anaglyph_orientation = AnaglyphOrientation.CyanRed) {
        const guess = guessCalibrationMeasurements();
        this.id = '';
        this.width_px = guess.width_px;
        this.height_px = guess.height_px;
        this.pixel_ratio = guess.pixel_ratio;
        this.height_cm = guess.height_cm;
        this.distance_cm = guess.distance_cm;
        this.anaglyph_orientation = default_anaglyph_orientation;
        this.colour_correction = defaultColourCorrection();
        this.fingerprint = guess.fingerprint;
        this.no_touch_screen = null;
    }

    refreshDeviceIdentity() {
        Object.assign(this, currentCalibrationDeviceIdentity());
    }

    // Store to local storage
    storeToLocal() {
        window.localStorage['opticalgym_calibration'] = JSON.stringify(this);
    }

    // Calculate the screen fingerprint
    static calcFingerprint(): string {
        return calibrationFingerprint();
    }

    static fromJson(data: Object): Calibration {
        let calibration = new Calibration();
        Object.assign(calibration, data);
        calibration.fingerprint =
            typeof (data as { fingerprint?: unknown }).fingerprint === "string"
                ? (data as { fingerprint: string }).fingerprint
                : "";
        return calibration;
    }
}

export class UserCalibrationMetadata {
    id: string | null = null;
    data: Calibration | null = null;
    user_id: string | null = null;
    clinic_id: string | null = null;
    category: string = 'calibration';

    static fromJson(data: { [key: string]: any }): UserCalibrationMetadata {
        let calibration_metadata = new UserCalibrationMetadata();
        Object.assign(calibration_metadata, data);
        calibration_metadata.data = Calibration.fromJson(data['data']);
        calibration_metadata.data.id = data['id'] || null;
        calibration_metadata.data.user_id = data['user_id'] || null;
        return calibration_metadata;
    }
}

export class CalibrationList {
    data: Calibration[];

    constructor() {
        this.data = [];
    }

    public getCurrentCalibration(): null | Calibration {
        const resolved_calibration = resolveStoredCalibration(
            this.data,
            window.localStorage['opticalgym_calibration'],
        );
        const calibration = resolved_calibration
            ? resolved_calibration instanceof Calibration
                ? resolved_calibration
                : Calibration.fromJson(resolved_calibration)
            : null;

        // Set local-storage cache
        if (calibration) {
            calibration.storeToLocal();
        }

        return calibration;
    }

    static fromResponse(response_data: Array<UserCalibrationMetadata>) {
        let calibration_list = new CalibrationList();

        for (var i in response_data) {
            let metadata = response_data[i];
            let calibration_data = metadata.data!;
            calibration_data.id = metadata.id;
            calibration_data.user_id = metadata.user_id;

            let calibration = Calibration.fromJson(calibration_data);
            calibration_list.data.push(calibration);
        }

        return calibration_list;
    }
}
